import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { APP_NAME } from "@/config";
import { useAuth } from "@/context/AuthContext";
import { useFlags } from "@/lib/flags";
import { addressLine } from "@/lib/address";
import { MIN_CONTRAST, WHITE, DOC_TEXT, hexColour, contrast, ratioLabel, palette } from "@/lib/brand";
import { useInvestors } from "@/components/Investors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { CircleCheck, TriangleAlert, Upload, Loader2 } from "lucide-react";

// Settings → Brand (design_handoff_founder_assistant/screens/Brand.dc.html, screen 7; backend/brand.py,
// FEATURE_DOCUMENTS): the logo, colours and name the assistant's documents carry, with the address from the billing
// details, and a live preview of an email's header, one section and its footer. Crewbench itself keeps its own look.
// Everything is saved at once with Save; a main colour too light to read keeps Save off.
const MAX_LOGO = 512 * 1024;
const DOC_FONT = "-apple-system, 'Segoe UI', Helvetica, Arial, sans-serif";
const HATCH = "repeating-linear-gradient(45deg, transparent 0 4px, rgba(128,128,128,0.35) 4px 5px)";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const size = (n) => (n < 1024 ? `${n} B` : `${Math.max(1, Math.round(n / 1024))} KB`);
const first = (name) => (name || "").trim().split(/\s+/)[0];
// "Hi Daniel and Priya,": the company's investors, as an investor update greets them
function greeting(investors) {
  const names = (investors || []).map((i) => first(i.name)).filter(Boolean);
  if (!names.length) return "Hello,";
  return `Hi ${names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`},`;
}

const readBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

// The colour fields' state: the note under each, and whether Save can go
function mainNote(value) {
  const hex = hexColour(value);
  if (hex === undefined) return { bad: true, text: "A colour is a hex code such as #1F5E4B." };
  if (hex === null) return { text: "Not set. Headings and numbers use dark grey." };
  const r = contrast(hex, WHITE);
  if (r < MIN_CONTRAST) return { bad: true, text: `Too light: text in it or on it is hard to read, ${ratioLabel(r)}:1 against white. Pick a darker colour, at least 4.5:1.` };
  return { ok: true, text: `Easy to read: ${ratioLabel(r)}:1 against white, as text or behind white text.` };
}

function accentNote(value) {
  const hex = hexColour(value);
  if (hex === undefined) return { bad: true, text: "A colour is a hex code such as #1F5E4B." };
  if (hex === null) return { text: "Not set. Used for small marks only." };
  const r = contrast(hex, DOC_TEXT);
  return r >= MIN_CONTRAST ? { ok: true, text: `Used for small marks. Dark text on it reads well: ${ratioLabel(r)}:1.` }
    : { text: "Used for small marks." };
}

function Row({ children, className }) {
  return <div className={cn("px-[18px] py-3.5 border-t flex flex-col gap-1.5", className)}>{children}</div>;
}

function ColourField({ id, label, value, onChange, note }) {
  const hex = hexColour(value);
  return (
    <Row>
      <label htmlFor={id} className="font-semibold">{label}</label>
      <span className="flex items-center gap-2">
        <label className="relative w-8 h-8 shrink-0 rounded-md border cursor-pointer focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring"
          style={{ background: hex || HATCH }} title={`Pick the ${label.toLowerCase()}`}>
          <input type="color" className="absolute inset-0 opacity-0 cursor-pointer" value={hex || "#1A1F1C"}
            aria-label={`Pick the ${label.toLowerCase()}`} onChange={(e) => onChange(e.target.value.toUpperCase())} />
        </label>
        <Input id={id} value={value} placeholder="Not set" spellCheck={false} autoComplete="off" dir="ltr"
          onChange={(e) => onChange(e.target.value)} onBlur={() => hex && onChange(hex)}
          aria-invalid={!!note.bad} aria-describedby={`${id}-note`}
          className={cn("w-[120px] tabular-nums", note.bad && "border-gr-danger")} data-testid={id} />
      </span>
      <span id={`${id}-note`} className={cn("flex gap-1.5 text-sm", note.bad ? "text-gr-danger" : "text-muted-foreground")}
        data-testid={`${id}-note`}>
        {note.ok && <CircleCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />}
        {note.bad && <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />}
        <span>{note.text}</span>
      </span>
    </Row>
  );
}

function LogoTile({ src, dark, caption }) {
  return (
    <figure className="m-0 flex flex-col gap-1">
      <span className="w-[180px] h-[72px] rounded-lg border grid place-items-center px-4" style={{ background: dark ? "#1A1D1B" : "#FFFFFF" }}>
        <img src={src} alt="" className="max-w-full max-h-12 object-contain" />
      </span>
      <figcaption className="text-sm text-muted-foreground">{caption}</figcaption>
    </figure>
  );
}

// The live preview: an investor update's header, its Progress section and its footer band, as the email shows them
function Preview({ name, colours, logoUrl, address, objectives }) {
  const { user } = useAuth();
  const investors = useInvestors()?.investors;
  const p = palette(colours);
  const now = new Date();
  const month = MONTHS[now.getMonth()];
  const thisMonth = now.toISOString().slice(0, 7);
  const objs = objectives || [];
  const doneThisMonth = objs.filter((o) => o.status === "done" && (o.done_at || "").slice(0, 7) === thisMonth).length;
  const brandName = name.trim() || "Your company";
  const mark = (h) => (logoUrl ? <img src={logoUrl} alt={brandName} style={{ maxHeight: h, maxWidth: 200 }} />
    : <span style={{ fontWeight: 700 }}>{brandName}</span>);
  const label = { fontSize: 11, letterSpacing: "0.08em", color: "#5C6560" };
  return (
    <section aria-label="Live preview" className="flex-[1_1_380px] min-w-0 flex flex-col gap-2.5" data-testid="brand-preview">
      <span className="text-xs uppercase tracking-[0.09em] text-muted-foreground">Live preview · Email header and footer</span>
      {/* the document's own look, in the company's brand: never Graphite's */}
      <div className="rounded-[10px] p-5" style={{ background: "#EEF0EC" }}>
        <div className="mx-auto max-w-[520px] rounded-lg overflow-hidden" style={{ background: "#FFFFFF", color: DOC_TEXT, fontFamily: DOC_FONT, fontSize: 15, lineHeight: "24px" }}>
          <div className="flex flex-col gap-5" style={{ padding: "32px 32px 20px" }}>
            <span style={{ display: "inline-flex", alignItems: "center", fontSize: 20, lineHeight: "26px" }}>{mark(32)}</span>
            <div className="flex flex-col gap-2">
              <span style={{ ...label, fontSize: 12, lineHeight: "16px" }}>INVESTOR UPDATE · {month.toUpperCase()} {now.getFullYear()}</span>
              <span>{greeting(investors)}</span>
              <span>Here is {brandName}'s update for {month}.</span>
            </div>
          </div>
          <div className="flex flex-col gap-2.5" style={{ padding: "20px 32px", borderTop: "1px solid #E3E6E2" }}>
            <span style={{ fontSize: 18, lineHeight: "24px", fontWeight: 700, color: p.main }}>Progress</span>
            <span className="flex gap-6">
              {[["DONE", objs.filter((o) => o.status === "done").length], ["IN REVIEW", objs.filter((o) => o.status === "review").length]].map(([k, n]) => (
                <span key={k} className="flex flex-col"><span style={label}>{k}</span>
                  <span style={{ fontSize: 22, lineHeight: "28px", fontWeight: 700, color: p.main }}>{n}</span></span>
              ))}
            </span>
            <span style={{ display: "block", background: p.tint, borderRadius: 8, padding: "10px 14px", fontSize: 14 }}>
              <span style={{ fontWeight: 700, color: p.main }}>Done in {month}</span> · {doneThisMonth} {doneThisMonth === 1 ? "objective" : "objectives"}
            </span>
          </div>
          <footer className="flex flex-col gap-2" style={{ background: p.foot, color: "#FFFFFF", padding: "20px 32px 24px", fontSize: 13, lineHeight: "20px" }}>
            <span style={{ display: "inline-flex", alignItems: "center" }}>{mark(20)}</span>
            {address && <span style={{ color: p.footMuted }}>{[address.name, addressLine(address)].filter(Boolean).join(" · ")}</span>}
            <span style={{ color: p.footMuted }}>You get this email because you invested in {brandName}. {user?.name || "Your founder"} sent it via {APP_NAME}.</span>
            <span style={{ color: "#FFFFFF", textDecoration: "underline" }}>Stop these scheduled updates</span>
          </footer>
        </div>
      </div>
    </section>
  );
}

export default function BrandSettings() {
  const qc = useQueryClient();
  const flags = useFlags();
  const { data } = useQuery({ queryKey: ["brand"], queryFn: async () => (await api.get("/brand")).data.brand });
  const { data: objs } = useQuery({ queryKey: ["objectives"], queryFn: async () => (await api.get("/objectives")).data });
  const hasSavedLogo = !!data?.logo;
  const { data: savedLogoUrl } = useQuery({
    queryKey: ["brand-logo", data?.logo?.updated_at], enabled: hasSavedLogo, staleTime: Infinity,
    queryFn: async () => URL.createObjectURL((await api.get("/brand/logo", { responseType: "blob" })).data),
  });
  const [form, setForm] = useState(null);  // {name, main, accent, logo: "saved" | {file, url} | null}
  useEffect(() => {
    if (data && !form) setForm({ name: data.name, main: data.main || "", accent: data.accent || "", logo: data.logo ? "saved" : null });
  }, [data, form]);
  const picked = form?.logo && form.logo !== "saved" ? form.logo : null;
  useEffect(() => () => picked && URL.revokeObjectURL(picked.url), [picked]);
  const fileInput = useRef(null);
  const [dropping, setDropping] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      const body = { name: form.name.trim(), main: hexColour(form.main), accent: hexColour(form.accent) };
      if (picked) body.logo = { name: picked.file.name, data: await readBase64(picked.file) };
      else if (form.logo === null && hasSavedLogo) body.remove_logo = true;
      return (await api.put("/brand", body)).data.brand;
    },
    onSuccess: (brand) => {
      qc.setQueryData(["brand"], brand);
      setForm({ name: brand.name, main: brand.main || "", accent: brand.accent || "", logo: brand.logo ? "saved" : null });
      toast.success("Saved. Your documents use it from now on.");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const notes = useMemo(() => form && { main: mainNote(form.main), accent: accentNote(form.accent) }, [form]);
  if (!data || !form) return null;

  const choose = (file) => {
    if (!file) return;
    const kind = file.type === "image/png" || /\.png$/i.test(file.name) ? "png" : file.type === "image/svg+xml" || /\.svg$/i.test(file.name) ? "svg" : null;
    if (!kind) { toast.error("A logo is a PNG or an SVG."); return; }
    if (file.size > MAX_LOGO) { toast.error("A logo can be at most 512 KB."); return; }
    setForm((f) => ({ ...f, logo: { file, url: URL.createObjectURL(file) } }));
  };
  const logoUrl = picked ? picked.url : form.logo === "saved" ? savedLogoUrl : null;
  const logoFacts = picked ? `${picked.file.name} · ${size(picked.file.size)}` : form.logo === "saved" ? `${data.logo.name} · ${size(data.logo.size)}` : "";
  const colours = { main: hexColour(form.main) || null, accent: hexColour(form.accent) || null };
  const bad = notes.main.bad || notes.accent.bad || !form.name.trim();
  const dirty = form.name.trim() !== data.name || colours.main !== (data.main || null) || colours.accent !== (data.accent || null)
    || !!picked || (form.logo === null && hasSavedLogo);
  const status = notes.main.bad || notes.accent.bad ? "One colour needs a fix before documents use it."
    : data.main || data.logo ? `Your documents use the ${data.name} brand.` : "Documents use a neutral look until you set a brand.";

  return (
    <div id="brand" className="scroll-mt-4 flex flex-wrap items-start gap-6" data-testid="settings-brand">
      <section aria-labelledby="brand-title" className="flex-[1_1_460px] max-w-[600px] min-w-0 bg-card rounded-[10px] flex flex-col">
        <div className="px-[18px] pt-4 pb-3 flex flex-col gap-0.5">
          <h2 id="brand-title" className="m-0 font-display text-[15px] leading-[22px] tracking-[0.02em] font-semibold">Brand</h2>
          <span className="text-sm text-muted-foreground">Goes on the documents your assistant prepares. {APP_NAME} itself keeps its own look.</span>
          <span className="pt-1.5 font-semibold" role="status" data-testid="brand-status">{status}</span>
        </div>

        <Row className="gap-2.5">
          <span className="font-semibold">Logo</span>
          <input ref={fileInput} type="file" accept="image/png,image/svg+xml,.png,.svg" className="hidden"
            onChange={(e) => { choose(e.target.files?.[0]); e.target.value = ""; }} data-testid="brand-logo-input" />
          {form.logo ? (<>
            <div className="flex flex-wrap gap-3">
              {logoUrl ? (<>
                <LogoTile src={logoUrl} caption="On light" />
                <LogoTile src={logoUrl} dark caption="On dark, as email apps show it" />
              </>) : <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" aria-label="Loading the logo" />}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={() => fileInput.current?.click()} data-testid="brand-logo-replace">Replace</Button>
              <Button variant="danger" onClick={() => setForm((f) => ({ ...f, logo: null }))} data-testid="brand-logo-remove">Remove</Button>
              <span className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{logoFacts}</span>
            </div>
          </>) : (
            <div className={cn("border border-dashed border-gr-control-border rounded-lg p-5 flex flex-col items-center gap-2 text-center",
              dropping && "bg-gr-hover")} data-testid="brand-logo-drop"
              onDragOver={(e) => { e.preventDefault(); setDropping(true); }} onDragLeave={() => setDropping(false)}
              onDrop={(e) => { e.preventDefault(); setDropping(false); choose(e.dataTransfer.files?.[0]); }}>
              <Upload className="w-5 h-5" aria-hidden="true" />
              <Button variant="outline" onClick={() => fileInput.current?.click()} data-testid="brand-logo-upload">Upload a logo</Button>
              <span className="text-sm text-muted-foreground">PNG or SVG, or drop it here. We show it on light and on dark.</span>
            </div>
          )}
        </Row>

        <Row>
          <label htmlFor="brand-name" className="font-semibold">Company name</label>
          <Input id="brand-name" value={form.name} maxLength={80} className="max-w-[320px]"
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="brand-name" />
        </Row>

        <ColourField id="brand-main" label="Main colour" value={form.main} note={notes.main} onChange={(v) => setForm((f) => ({ ...f, main: v }))} />
        <ColourField id="brand-accent" label="Accent colour" value={form.accent} note={notes.accent} onChange={(v) => setForm((f) => ({ ...f, accent: v }))} />

        <Row className="gap-1">
          <span className="font-semibold">Address</span>
          {data.address && <span data-testid="brand-address">{[data.address.name, addressLine(data.address)].filter(Boolean).join(", ")}</span>}
          <span className="text-sm text-muted-foreground">Taken from your billing details.{flags.store && <> <Link to="/founder/store#billing-history"
            className="text-foreground font-semibold underline underline-offset-2 hover:opacity-50">{data.address ? "Change it in Billing" : "Add them in Billing"}</Link></>}</span>
        </Row>

        <div className="px-[18px] py-3.5 border-t flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={() => setForm((f) => ({ ...f, main: "", accent: "", logo: null }))} data-testid="brand-neutral">Use the neutral look</Button>
          <Button disabled={bad || !dirty || save.isPending} onClick={() => save.mutate()} data-testid="brand-save">
            {save.isPending && <Loader2 className="animate-spin" />}Save
          </Button>
        </div>
      </section>
      <Preview name={form.name} colours={colours} logoUrl={logoUrl} address={data.address} objectives={objs?.objectives} />
    </div>
  );
}
